/**
 * NosAKESGRisk — /nosak/esg
 * Institutional ESG command center: risk heatmap, IFRS S2 readiness,
 * TCFD scoring, governance maturity, disclosure tracker, report outputs.
 */
import React, { useState } from "react";
import { useNosak } from "@/context/NOSAKContext";
import { toast } from "sonner";
import { Download, Loader, CheckCircle2, XCircle, AlertTriangle, Shield, FileText, Activity } from "lucide-react";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n/1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtPct = v => `${Number(v || 0).toFixed(1)}%`;

const TABS = [
  { id: "risk",       label: "Climate Risk" },
  { id: "disclosure", label: "IFRS S2 / TCFD" },
  { id: "governance", label: "Governance" },
  { id: "reports",    label: "Reports" },
];

// Risk severity: 1=Low 2=Med 3=High
const RISK_MATRIX = [
  { category: "Physical", risks: [
    { name: "Flooding (Delta/Edo)", type: "Acute",   severity: 3, horizon: "Near", mitigation: "Site elevation + BCP", status: "Identified" },
    { name: "Drought (Palm yield)", type: "Chronic", severity: 2, horizon: "Long", mitigation: "Irrigation + crop variety", status: "Planned" },
    { name: "Temperature (process)", type:"Chronic", severity: 1, horizon: "Long", mitigation: "Thermal monitoring", status: "Monitoring" },
  ]},
  { category: "Transition", risks: [
    { name: "HFO supply/price", type: "Market",      severity: 3, horizon: "Near", mitigation: "Biomass conversion programme", status: "Active" },
    { name: "Investor ESG req.", type: "Reputation",  severity: 2, horizon: "Near", mitigation: "IFRS S2 + TCFD disclosure", status: "In progress" },
    { name: "Carbon pricing",   type: "Policy",       severity: 2, horizon: "Med",  mitigation: "Carbon asset positioning", status: "Planned" },
    { name: "Sustainable procurement", type: "Market",severity: 1, horizon: "Med",  mitigation: "Green certification", status: "Planned" },
  ]},
];

const SEV_STYLE = {
  3: { label: "High",   bg: "#fef2f2", text: "#991b1b", bar: "#dc2626" },
  2: { label: "Medium", bg: "#fffbeb", text: "#92400e", bar: "#d97706" },
  1: { label: "Low",    bg: "#ecfdf5", text: "#065f46", bar: "#059669" },
};

export default function NosAKESGRisk() {
  const { group, loading, selectedPeriod } = useNosak();
  const [activeTab, setActiveTab] = useState("risk");
  const [downloading, setDownloading] = useState(null);

  const downloadPdf = async (type) => {
    setDownloading(type);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/nosak/pdf/${type}/${selectedPeriod}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } }
      );
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `Nosak_${type}_${selectedPeriod}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(null); }
  };

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading…</div>;

  const scope = group.scope_split || {};
  const conf = group.data_confidence_avg || 0;
  const qs = group.qualification_summary || {};

  // IFRS S2 / TCFD readiness scoring
  const ifrsChecklist = [
    { item: "Scope 1+2 quantified (SSOT)",        done: true  },
    { item: "Governance — board oversight defined",done: true  },
    { item: "Strategy — short/medium/long-term",  done: true  },
    { item: "Scenario analysis (3 pathways)",      done: true  },
    { item: "Scope 3 boundary documented",         done: "partial" },
    { item: "T1 measured evidence (priority sources)", done: false },
    { item: "Third-party verification conducted",  done: false },
    { item: "TCFD-aligned public board report",    done: false },
  ];
  const ifrsComplete = ifrsChecklist.filter(c => c.done === true).length;
  const ifrsPct = Math.round(ifrsComplete / ifrsChecklist.length * 100);

  const tcfdPillars = [
    { pillar: "Governance",        score: 75, note: "Board Committee defined. Annual review active." },
    { pillar: "Strategy",          score: 60, note: "Scenarios modelled. DFI engagement not yet initiated." },
    { pillar: "Risk Management",   score: 55, note: "Risks identified. Integration process in progress." },
    { pillar: "Metrics & Targets", score: 45, note: "Baseline quantified. Formal 2030 targets not adopted." },
  ];

  const govItems = [
    { item: "Board Climate Committee", status: "Active",      color: "#059669" },
    { item: "Annual ESG Review Cycle", status: "Active",      color: "#059669" },
    { item: "Subsidiary MD Accountability", status: "Active", color: "#059669" },
    { item: "T1 Evidence Collection",   status: "In Progress",color: "#d97706" },
    { item: "SSOT Calculation Audit Trail", status: "Active", color: "#059669" },
    { item: "Third-Party Verification", status: "Not Started",color: "#dc2626" },
    { item: "Carbon Methodology Registration", status: "Planned", color: "#d97706" },
    { item: "Public IFRS S2 Disclosure", status: "Not Started",color: "#dc2626" },
  ];

  return (
    <div className="p-5 lg:p-6 bg-[#F8FAFC] min-h-screen space-y-4">
      <div>
        <h1 className="text-xl font-black text-slate-900">ESG, Risk & Disclosure</h1>
        <p className="text-[10px] text-slate-400 mt-0.5">IFRS S2 · TCFD · Climate risk · Governance maturity · {selectedPeriod}</p>
      </div>

      {/* ESG KPI Strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "IFRS S2 Readiness", value: `${ifrsPct}%`, color: ifrsPct >= 70 ? "#059669" : "#d97706", sub: `${ifrsComplete}/${ifrsChecklist.length} items complete` },
          { label: "Disclosure Confidence", value: conf.toFixed(0)+"/100", color: conf>=65?"#059669":"#d97706", sub: conf>=65?"T2 declared basis":"Below verification threshold" },
          { label: "Physical Risk Exposure", value: "Medium-High", color: "#dc2626", sub: "Flooding primary risk (Delta/Edo)" },
          { label: "Transition Risk",         value: "High",        color: "#dc2626", sub: "HFO supply — near-term material" },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
            <p className="text-[9px] text-slate-400 uppercase tracking-wider">{label}</p>
            <p className="text-lg font-black mt-0.5" style={{ color }}>{value}</p>
            <p className="text-[9px] text-slate-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1.5">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${activeTab === t.id ? "bg-[#0f172a] text-white" : "text-slate-500 hover:bg-slate-50"}`}
            data-testid={`tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {/* CLIMATE RISK */}
      {activeTab === "risk" && (
        <div className="space-y-3">
          {RISK_MATRIX.map(cat => (
            <div key={cat.category} className="bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                <p className="text-[10px] font-bold text-slate-700">{cat.category} Risk</p>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    {["Risk", "Type", "Severity", "Horizon", "Mitigation", "Status"].map(h => (
                      <th key={h} className="text-left py-1.5 px-3 text-slate-500 font-semibold text-[10px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cat.risks.map(r => {
                    const s = SEV_STYLE[r.severity];
                    return (
                      <tr key={r.name} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3 font-medium text-slate-800 text-[10px]">{r.name}</td>
                        <td className="py-2 px-3 text-slate-500 text-[10px]">{r.type}</td>
                        <td className="py-2 px-3">
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded"
                            style={{ background: s.bg, color: s.text }}>{s.label}</span>
                        </td>
                        <td className="py-2 px-3 text-slate-500 text-[10px]">{r.horizon}-term</td>
                        <td className="py-2 px-3 text-[10px] text-slate-600 max-w-[160px]">{r.mitigation}</td>
                        <td className="py-2 px-3">
                          <span className={`text-[9px] font-medium ${r.status==="Active"?"text-emerald-600":r.status==="Planned"?"text-blue-600":"text-amber-600"}`}>
                            {r.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* IFRS S2 / TCFD */}
      {activeTab === "disclosure" && (
        <div className="grid grid-cols-2 gap-4">
          {/* IFRS S2 readiness */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-slate-700">IFRS S2 Readiness Tracker</p>
              <div className="flex items-center gap-2">
                <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: ifrsPct+"%" }} />
                </div>
                <span className={`text-[10px] font-black ${ifrsPct>=70?"text-emerald-600":"text-amber-600"}`}>{ifrsPct}%</span>
              </div>
            </div>
            {ifrsChecklist.map((c, i) => (
              <div key={i} className="flex items-center gap-2.5 py-1.5 border-b border-slate-50 last:border-0">
                {c.done === true
                  ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0" />
                  : c.done === "partial"
                  ? <div className="w-2.5 h-2.5 rounded-full border-2 border-amber-400 flex-shrink-0" />
                  : <XCircle size={11} className="text-slate-300 flex-shrink-0" />}
                <p className={`text-[10px] ${c.done===true?"text-slate-700":c.done==="partial"?"text-amber-700":"text-slate-400"}`}>{c.item}</p>
              </div>
            ))}
          </div>

          {/* TCFD pillar scoring */}
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">TCFD Pillar Scoring</p>
            {tcfdPillars.map(p => {
              const pColor = p.score >= 70 ? "#059669" : p.score >= 50 ? "#d97706" : "#dc2626";
              return (
                <div key={p.pillar} className="mb-3">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-[10px] font-bold text-slate-700">{p.pillar}</p>
                    <span className="text-[10px] font-black" style={{ color: pColor }}>{p.score}/100</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-0.5">
                    <div className="h-full rounded-full" style={{ width: p.score+"%", background: pColor }} />
                  </div>
                  <p className="text-[9px] text-slate-400">{p.note}</p>
                </div>
              );
            })}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 mt-2">
              <p className="text-[9px] text-blue-700">
                Overall TCFD Alignment: {Math.round(tcfdPillars.reduce((s,p) => s+p.score,0)/tcfdPillars.length)}/100.
                Target: ≥70 for institutional investor acceptance.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* GOVERNANCE */}
      {activeTab === "governance" && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
            <p className="text-xs font-bold text-slate-700 mb-3">Governance Item Status</p>
            {govItems.map(g => (
              <div key={g.item} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                <p className="text-[10px] text-slate-600">{g.item}</p>
                <span className="text-[9px] font-bold px-2 py-0.5 rounded flex-shrink-0 ml-2"
                  style={{ background: g.color+"15", color: g.color }}>{g.status}</span>
              </div>
            ))}
          </div>
          <div className="space-y-3">
            <div className="bg-white border border-slate-100 rounded-xl p-4 shadow-sm">
              <p className="text-xs font-bold text-slate-700 mb-2">Climate Targets (Adopted)</p>
              {[
                ["FY Baseline", fmtN(group.total_tco2e, "tCO₂e/yr")],
                ["2030 Target (Moderate)", `−${Math.round((group.reduction_potential_tco2e||0)*0.5/(group.total_tco2e||1)*100)}% Scope 1+2`],
                ["Circularity Target", "≥65/100 by 2028"],
                ["Evidence Target", "≥65 confidence by Q4 2026"],
                ["Carbon Programme", "First VCS registration Q4 2027"],
              ].map(([k,v]) => (
                <div key={k} className="flex justify-between text-[10px] py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-bold text-slate-700">{v}</span>
                </div>
              ))}
            </div>
            <div className="bg-slate-900 rounded-xl p-4 text-white">
              <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-2">SSOT Audit Trail Status</p>
              {["All calculations SSOT-immutable","Input changes logged with timestamp","No manual overrides permitted","Report hashes QR-verifiable","DeCarbonIQ platform maintains full history"].map(a => (
                <div key={a} className="flex items-center gap-1.5 py-1 border-b border-white/10 last:border-0">
                  <CheckCircle2 size={10} className="text-emerald-400 flex-shrink-0" />
                  <p className="text-[9px] opacity-70">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* REPORTS */}
      {activeTab === "reports" && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { key:"executive",           label:"Executive Climate Intelligence", badge:"Board",      desc:"Group emissions, subsidiaries, SDG, projects, methodology. 10 pages." },
            { key:"industrial-emissions",label:"Industrial Emissions",           badge:"Technical",  desc:"Scope 1/2/3 breakdown, intensity, methodology. 5 pages." },
            { key:"circularity",         label:"Circularity & Waste",            badge:"Operations", desc:"Biomass chain, POME, regenerative readiness. 5 pages." },
            { key:"sustainable-finance", label:"Sustainable Finance",            badge:"ICMA GBP",   desc:"Capital stack, DSCR, qualification tiers. 6 pages." },
            { key:"carbon-monetisation", label:"Carbon Monetisation",            badge:"VCM/Art.6",  desc:"4-tier qualification, mandatory disclosures. 5 pages." },
            { key:"ifrs-s2",             label:"IFRS S2 / TCFD Disclosure",     badge:"Governance", desc:"TCFD pillars, risk register, metrics. 5 pages." },
          ].map(r => (
            <div key={r.key} className="bg-white border border-slate-100 rounded-xl p-3.5 shadow-sm flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={11} className="text-slate-400 flex-shrink-0" />
                  <p className="text-[10px] font-bold text-slate-800">{r.label}</p>
                  <span className="text-[8px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-bold flex-shrink-0">{r.badge}</span>
                </div>
                <p className="text-[9px] text-slate-500">{r.desc}</p>
              </div>
              <button onClick={() => downloadPdf(r.key)} disabled={downloading===r.key}
                className="flex items-center gap-1 bg-[#1d4ed8] text-white text-[9px] px-2.5 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-60 flex-shrink-0 font-bold"
                data-testid={`download-${r.key}`}>
                {downloading===r.key ? <Loader size={9} className="animate-spin"/> : <Download size={9}/>} PDF
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
